import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getReleaseDetails } from "../../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../../helpers/aws_api_helper";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Same `fileName` as Configuration → Add-ons → FinOps (Cost Insight) manual configuration
 * (`getReleaseDetails` / `GET cloudnova/documentation?fileName=...`).
 */
const getFinOpsManualGuideFileName = () => {
  const fromEnv = process.env.REACT_APP_COST_INSIGHT_MANUAL_CONFIGURATION_GUIDE;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim();
  }
  return "configurations/Cost_Insight_Manual_Guide.pdf";
};

/** Manual panel max width (right). Uses min(px, vw) so layout stays usable under browser zoom. */
const OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX = 600;
const OFFLINE_CUR_MANUAL_PANEL_WIDTH = `min(${OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px, 40vw)`;
const OFFLINE_CUR_DIALOG_MR_WITH_MANUAL = `min(${24 + OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px, calc(40vw + 24px))`;
/** Above MUI Dialog (1300) so Confirm stays clickable over the manual panel. */
const OFFLINE_CUR_MANUAL_Z_INDEX = 1400;
const OFFLINE_CUR_CONFIRM_Z_INDEX = 1500;

/** `GET cloudnova/documentation` may return the signed URL in `url`, `signedUrl`, or other keys. */
const isHttpUrlString = (v) => typeof v === "string" && /^https?:\/\//i.test(v.trim());

const pickDocumentationUrlFromReleaseResponse = (response) => {
  if (response == null) return "";
  const status = Number(response.status);
  if (Number.isNaN(status) || status !== 200) return "";
  const named = [
    response.url,
    response.signedUrl,
    response.documentUrl,
    response.fileUrl,
    response.link,
    response.href,
  ];
  for (const v of named) {
    if (isHttpUrlString(v)) return v.trim();
  }
  const skip = new Set(["status", "message", "error", "data", "isSynthetic"]);
  if (response && typeof response === "object") {
    for (const [k, v] of Object.entries(response)) {
      if (skip.has(k) || v == null) continue;
      if (isHttpUrlString(v)) return String(v).trim();
    }
  }
  return "";
};

const UploadOfflineCurModal = ({
  open,
  onClose,
  onSubmit,
  isUploading,
  uploadProgress,
  onRequestCancelUpload,
  accountNumbers = [],
}) => {
  const now = new Date();
  const [accountNumber, setAccountNumber] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formError, setFormError] = useState("");
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [manualPanelOpen, setManualPanelOpen] = useState(true);

  const [finOpsManualGuideUrl, setFinOpsManualGuideUrl] = useState("");
  const [finOpsManualGuideLoading, setFinOpsManualGuideLoading] = useState(false);
  const [finOpsManualGuideError, setFinOpsManualGuideError] = useState("");
  /** Alias: API-fetched document URL (some bundles/hot-reloads may still reference the old name). */
  const finOpsManualGuideSrc = finOpsManualGuideUrl;

  useEffect(() => {
    if (!open) {
      setFinOpsManualGuideUrl("");
      setFinOpsManualGuideLoading(false);
      setFinOpsManualGuideError("");
      setUploadConfirmOpen(false);
      setManualPanelOpen(true);
      return;
    }
    setManualPanelOpen(true);

    let cancelled = false;
    const fileName = getFinOpsManualGuideFileName();

    (async () => {
      setFinOpsManualGuideLoading(true);
      setFinOpsManualGuideError("");
      setFinOpsManualGuideUrl("");
      try {
        const response = await getReleaseDetails(fileName);
        if (cancelled) return;
        const docUrl = pickDocumentationUrlFromReleaseResponse(response);
        if (docUrl) {
          setFinOpsManualGuideUrl(docUrl);
        } else {
          setFinOpsManualGuideError(
            getApiErrorMessage(response) || "Could not load the FinOps manual document."
          );
        }
      } catch (e) {
        if (!cancelled) {
          setFinOpsManualGuideError(
            getApiErrorMessage(e?.data ?? e) || e?.message || "Could not load the FinOps manual document."
          );
        }
      } finally {
        if (!cancelled) setFinOpsManualGuideLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];
  }, []);

  const accountNumberOptions = useMemo(() => {
    if (!Array.isArray(accountNumbers)) return [];
    const seen = new Set();
    const list = [];
    accountNumbers.forEach((x) => {
      const s = x != null ? String(x).trim() : "";
      if (s && !seen.has(s)) {
        seen.add(s);
        list.push(s);
      }
    });
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [accountNumbers]);

  const normalizedAccountNumber = accountNumber.trim();
  const isValidAccountNumber = /^\d{12}$/.test(normalizedAccountNumber);

  const organizationDisplayName = useMemo(() => {
    try {
      const raw = localStorage.getItem("selectedOrganization");
      if (!raw) return "";
      const o = JSON.parse(raw);
      const n = o?.orgName ?? o?.organizationName ?? o?.org_name;
      return n != null ? String(n).trim() : "";
    } catch {
      return "";
    }
  }, [open]);

  const isFormComplete =
    isValidAccountNumber && Boolean(year) && Boolean(month) && selectedFiles.length > 0;

  const handleFileChange = (event) => {
    const list = Array.from(event.target.files || []);
    if (list.length === 0) {
      setSelectedFiles([]);
      if (event.target) event.target.value = "";
      return;
    }
    const valid = list.filter((f) => f.name.toLowerCase().endsWith(".gz"));
    if (valid.length === 0) {
      setFormError("Only gzip files (.gz) are supported.");
      setSelectedFiles([]);
    } else {
      if (valid.length < list.length) {
        setFormError(`${list.length - valid.length} file(s) skipped (not .gz).`);
      } else {
        setFormError("");
      }
      setSelectedFiles(valid);
    }
    if (event.target) event.target.value = "";
  };

  const resetModalState = () => {
    setAccountNumber("");
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedFiles([]);
    setFormError("");
  };

  const finishClose = () => {
    resetModalState();
    onClose();
  };

  const handleClose = () => {
    if (isUploading) {
      onRequestCancelUpload?.();
      return;
    }
    finishClose();
  };

  const validateBeforeUpload = () => {
    if (!normalizedAccountNumber) {
      setFormError("Account number is required.");
      return false;
    }
    if (!isValidAccountNumber) {
      setFormError("Account number must be exactly 12 digits.");
      return false;
    }
    if (selectedFiles.length === 0) {
      setFormError("Please select at least one file to upload.");
      return false;
    }
    if (selectedFiles.some((f) => !f.name.toLowerCase().endsWith(".gz"))) {
      setFormError("Only gzip files (.gz) are supported.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleUploadClick = () => {
    if (!validateBeforeUpload()) return;
    setUploadConfirmOpen(true);
  };

  const handleConfirmUpload = async () => {
    setUploadConfirmOpen(false);
    setFormError("");
    const success = await onSubmit({
      accountNumber: normalizedAccountNumber,
      year,
      month,
      files: selectedFiles,
    });

    if (success) {
      finishClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Backdrop click must not close the upload stepper (users often click away from the manual).
        if (reason === "backdropClick") return;
        if (isUploading) {
          onRequestCancelUpload?.();
          return;
        }
        finishClose();
      }}
      fullWidth
      maxWidth="sm"
      /* Centered dialog; scrollable under zoom so actions stay reachable. */
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "center",
          alignItems: "center",
          overflowX: "hidden",
          overflowY: "auto",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
          ...(manualPanelOpen
            ? { mr: { md: OFFLINE_CUR_DIALOG_MR_WITH_MANUAL } }
            : {}),
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {"Upload CUR2.0"}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
          disabled={isUploading}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto", minHeight: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <Alert
            severity="info"
            sx={{
              fontSize: "0.8125rem",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Box component="span" sx={{ display: "block" }}>
              • Enter the CUR 2.0 generated account number.
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Supports .gz format CUR 2.0 files ONLY.
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Supports uploading multiple CUR 2.0 files for the same month.
            </Box>
          </Alert>
          <Autocomplete
            freeSolo
            size="small"
            options={accountNumberOptions}
            inputValue={accountNumber}
            onInputChange={(event, newInputValue) =>
              setAccountNumber((newInputValue ?? "").replace(/\D/g, "").slice(0, 12))
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account number"
                placeholder="Type or select an account"
                error={Boolean(normalizedAccountNumber) && !isValidAccountNumber}
                helperText={
                  Boolean(normalizedAccountNumber) && !isValidAccountNumber
                    ? "Account number must be exactly 12 digits."
                    : ""
                }
              />
            )}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                label="Month"
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {monthNames.map((monthName, index) => (
                  <MenuItem key={monthName} value={index + 1}>
                    {monthName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearOptions.map((optionYear) => (
                  <MenuItem key={optionYear} value={optionYear}>
                    {optionYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <input
              id="offline-cur-file-input"
              type="file"
              multiple
              accept=".gz,application/gzip"
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={handleFileChange}
            />
            <label htmlFor="offline-cur-file-input" style={isUploading ? { pointerEvents: "none" } : undefined}>
              <Button
                component="span"
                variant="outlined"
                disabled={isUploading}
                sx={{ textTransform: "none" }}
              >
                {selectedFiles.length > 0 ? "Change file(s)" : "Choose file(s)"}
              </Button>
            </label>
            {selectedFiles.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  mt: 1,
                  pl: 2.25,
                  m: 0,
                  maxHeight: 160,
                  overflowY: "auto",
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  listStyle: "disc",
                }}
              >
                {selectedFiles.map((f) => (
                  <Box
                    key={`${f.name}-${f.size}-${f.lastModified}`}
                    component="li"
                    sx={{ wordBreak: "break-all", display: "list-item" }}
                  >
                    {f.name}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: "#64748b" }}>No file(s) selected</Typography>
            )}
          </Box>
          {isUploading && !uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="indeterminate" sx={{ borderRadius: 1, height: 8 }} />
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                {uploadProgress?.fileCount > 1
                  ? `Preparing file ${uploadProgress.fileIndex} of ${uploadProgress.fileCount}…`
                  : "Preparing…"}
              </Typography>
              {uploadProgress?.fileCount > 1 && uploadProgress?.currentFileName ? (
                <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", wordBreak: "break-all" }}>
                  {uploadProgress.currentFileName}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {isUploading && uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (uploadProgress.completed / uploadProgress.total) * 100)}
                sx={{ borderRadius: 1, height: 8 }}
              />
              {uploadProgress?.fileCount > 1 && (
                <Typography sx={{ mt: 0.5, fontSize: "0.7rem", color: "#64748b", wordBreak: "break-all" }}>
                  File {uploadProgress.fileIndex} of {uploadProgress.fileCount}
                  {uploadProgress.currentFileName ? `: ${uploadProgress.currentFileName}` : ""}
                </Typography>
              )}
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                Uploading part {uploadProgress.completed} of {uploadProgress.total}…
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {isUploading ? "Cancel upload" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleUploadClick}
          disabled={isUploading || !isFormComplete}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {isUploading ? (uploadProgress?.total ? "Uploading…" : "Preparing…") : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={uploadConfirmOpen}
      onClose={() => setUploadConfirmOpen(false)}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
      sx={{ zIndex: OFFLINE_CUR_CONFIRM_Z_INDEX }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Confirm upload</DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto", minHeight: 0 }}>
        <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.6 }} component="div">
          {selectedFiles.length === 1 ? (
            <>
              This file <strong>{selectedFiles[0].name}</strong> will be uploaded into the system for CUR
              processing for account number : <strong>{normalizedAccountNumber}</strong> and your organization
              : <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : selectedFiles.length > 1 ? (
            <>
              These files:{" "}
              {selectedFiles.map((f, i) => (
                <React.Fragment key={`${f.name}-${f.size}-${f.lastModified}`}>
                  {i > 0 ? ", " : null}
                  <strong>{f.name}</strong>
                </React.Fragment>
              ))}{" "}
              will be uploaded into the system for CUR processing for account number :{" "}
              <strong>{normalizedAccountNumber}</strong> and your organization :{" "}
              <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : null}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Button onClick={() => setUploadConfirmOpen(false)} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirmUpload} sx={{ textTransform: "none", fontWeight: 600 }}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    {open && manualPanelOpen
      ? createPortal(
          <Paper
        elevation={8}
        sx={{
          /* Portaled to `document.body` so page/layout overflow, transforms, and nested scroll
           * regions never clip the PDF. Full viewport height gives the browser PDF bar room. */
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          left: { xs: 0, md: "auto" },
          width: { xs: "100vw", md: OFFLINE_CUR_MANUAL_PANEL_WIDTH },
          minWidth: 0,
          maxWidth: "100vw",
          maxHeight: "100dvh",
          zIndex: OFFLINE_CUR_MANUAL_Z_INDEX,
          borderRadius: 0,
          border: "1px solid #e5e7eb",
          borderTop: 0,
          borderBottom: 0,
          borderRight: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minHeight: 40,
            px: 1.5,
            py: 0.5,
            backgroundColor: "#f1f5f9",
            borderBottom: "1px solid #e2e8f0",
            flexShrink: 0,
            zIndex: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#0f172a",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title="CUR 2.0 manual"
          >
            CUR 2.0 manual
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {finOpsManualGuideSrc ? (
              <Button
                component="a"
                size="small"
                color="primary"
                variant="contained"
                startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
                href={finOpsManualGuideSrc}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  py: 0.35,
                  px: 1.25,
                }}
              >
                Open in new tab
              </Button>
            ) : null}
            <IconButton
              onClick={() => setManualPanelOpen(false)}
              size="small"
              aria-label="Close CUR 2.0 manual"
              sx={{ color: "#64748b" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {finOpsManualGuideError ? (
          <Box sx={{ px: 1.5, py: 0.5, flexShrink: 0 }}>
            <Alert severity="error" sx={{ fontSize: "0.75rem", py: 0, "& .MuiAlert-message": { padding: 0.75 } }}>
              {finOpsManualGuideError}
            </Alert>
          </Box>
        ) : null}
        {finOpsManualGuideLoading && !finOpsManualGuideSrc && !finOpsManualGuideError ? (
          <Box
            sx={{
              flex: "1 1 0%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e2e8f0",
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ mt: 1.5, fontSize: "0.8125rem", color: "#64748b" }}>
              Loading manual preview…
            </Typography>
          </Box>
        ) : null}
        {finOpsManualGuideSrc ? (
        <Box
          sx={{
            position: "relative",
            /* flex-basis 0% avoids some browsers giving this row 0 available height. */
            flex: "1 1 0%",
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            /* Filled by absolutely positioned iframe so flex/overflow don’t clip the viewer. */
            overflow: "visible",
          }}
        >
          <Box
            component="iframe"
            title="FinOps manual configuration"
            src={finOpsManualGuideSrc}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
              backgroundColor: "#e2e8f0",
            }}
          />
        </Box>
        ) : !finOpsManualGuideError && !finOpsManualGuideLoading ? (
          <Box sx={{ flex: 1, p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>No document URL returned.</Typography>
          </Box>
        ) : null}
      </Paper>,
          document.body
        )
      : null}
    </>
  );
};

export default UploadOfflineCurModal;
